import React, { useId } from 'react';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  getVisiblePageNumbers,
  shouldShowPagination,
} from '../utils/pagination';

/**
 * Central list pagination bar — use this on every list/table page.
 * Do not build custom Prev/Next UI elsewhere.
 *
 * Required for the full pattern: page, totalPages, count, onPageChange,
 * pageSize, onPageSizeChange.
 */

export function RowsPerPageSelect({
  pageSize,
  onPageSizeChange,
  disabled = false,
  id,
  className = '',
}) {
  const autoId = useId();
  const selectId = id || `rows-per-page-${autoId}`;
  const size = Number(pageSize) || DEFAULT_PAGE_SIZE;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <label htmlFor={selectId} className="text-sm text-ink-muted whitespace-nowrap">
        Rows per page:
      </label>
      <select
        id={selectId}
        value={size}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        disabled={disabled}
        aria-label="Rows per page"
        className="min-h-[44px] rounded-ds-xl border border-line bg-surface px-3 py-2 text-sm text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 sm:min-h-0"
      >
        {PAGE_SIZE_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

function PageNumberNav({ page, totalPages, onPageChange, disabled }) {
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pages = getVisiblePageNumbers(safePage, totalPages);
  const atFirst = safePage <= 1;
  const atLast = safePage >= totalPages;

  const pageBtnBase =
    'inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-lg border text-sm font-bold tabular-nums transition-all sm:min-h-[34px] sm:min-w-[34px]';

  return (
    <nav className="flex flex-wrap items-center justify-center gap-1.5" aria-label="Page navigation">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, safePage - 1))}
        disabled={disabled || atFirst}
        aria-label="Previous page"
        className={`${pageBtnBase} border-info-100 bg-info-50 text-info-800 shadow-sm hover:bg-info-100 disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-muted disabled:text-ink-subtle`}
      >
        ‹
      </button>

      {pages.map((item, index) =>
        item === 'ellipsis' ? (
          <span
            key={`ellipsis-${index}`}
            className="inline-flex min-w-[28px] items-center justify-center px-1 text-sm font-semibold text-ink-subtle"
            aria-hidden
          >
            …
          </span>
        ) : item === safePage ? (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            disabled={disabled}
            aria-label={`Page ${item}, current page`}
            aria-current="page"
            className={`${pageBtnBase} min-w-[40px] scale-105 border-brand-700 bg-brand-600 text-white shadow-md ring-2 ring-brand-200`}
          >
            {item}
          </button>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            disabled={disabled}
            aria-label={`Page ${item}`}
            className={`${pageBtnBase} border-line bg-surface text-ink hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700`}
          >
            {item}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
        disabled={disabled || atLast}
        aria-label="Next page"
        className={`${pageBtnBase} border-success-100 bg-success-50 text-success-800 shadow-sm hover:bg-success-100 disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-muted disabled:text-ink-subtle`}
      >
        ›
      </button>
    </nav>
  );
}

function shellClass(embedded, position, className) {
  const embeddedPositionClasses = embedded
    ? position === 'top'
      ? 'rounded-none border-x-0 border-t-0 border-b border-line'
      : 'rounded-none border-x-0 border-b-0 border-t border-line'
    : '';
  return `rounded-ds-xl border border-line bg-surface px-4 py-3 ${embeddedPositionClasses} ${className || ''}`.trim();
}

/**
 * @param {object} props
 * @param {number} props.page
 * @param {number} props.totalPages
 * @param {function} props.onPageChange
 * @param {number} [props.pageSize] — defaults to DEFAULT_PAGE_SIZE; always show selector when onPageSizeChange provided
 * @param {function} [props.onPageSizeChange]
 * @param {number} [props.count]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.embedded]
 */
function TablePagination({
  page,
  totalPages,
  onPageChange,
  pageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
  disabled = false,
  className = '',
  count = 0,
  rowsPerPageId,
  position = 'bottom',
  embedded = false,
}) {
  const itemCount = Number(count) || 0;
  const size = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const pages = Math.max(1, Number(totalPages) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), pages);
  const hasSizeControl = typeof onPageSizeChange === 'function';
  const showBar = itemCount > 0 || hasSizeControl;

  if (!showBar || typeof onPageChange !== 'function') {
    return null;
  }

  const viewedCount = itemCount > 0 ? Math.min(safePage * size, itemCount) : 0;
  const showPageNav = shouldShowPagination(pages, Math.max(itemCount, hasSizeControl ? 1 : 0));

  return (
    <div
      className={shellClass(embedded, position, className)}
      aria-label="Table pagination"
      data-list-pagination=""
    >
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3">
        {hasSizeControl ? (
          <RowsPerPageSelect
            pageSize={size}
            onPageSizeChange={onPageSizeChange}
            disabled={disabled}
            id={rowsPerPageId}
          />
        ) : null}

        <p
          className="text-sm font-semibold text-ink whitespace-nowrap tabular-nums"
          aria-live="polite"
          aria-label={`Showing ${viewedCount} of ${itemCount} records, page ${safePage} of ${pages}`}
        >
          {viewedCount.toLocaleString('en-IN')}
          <span className="mx-1 font-normal text-ink-subtle">/</span>
          {itemCount.toLocaleString('en-IN')}
          <span className="ml-2 rounded-md bg-brand-100 px-2 py-0.5 text-xs font-bold text-brand-700">
            Page {safePage}
          </span>
        </p>

        {showPageNav ? (
          <PageNumberNav
            page={safePage}
            totalPages={pages}
            onPageChange={onPageChange}
            disabled={disabled}
          />
        ) : null}
      </div>
    </div>
  );
}

export default TablePagination;
